// apps/support-console/src/App.tsx
/**
 * AYILand Support Agent Console — single-file SPA.
 *
 *   ┌─────────────────────────────────────────────────────────────────────┐
 *   │  Top bar — brand · presence · language · theme · logout              │
 *   ├──────────────┬──────────────────────────────────────┬───────────────┤
 *   │  Queue       │  Conversation                        │  Context      │
 *   │  (scope tabs)│  (messages + composer + agent ctrls) │  (user info)  │
 *   └──────────────┴──────────────────────────────────────┴───────────────┘
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Send,
  Sun,
  Moon,
  LogOut,
  Inbox,
  CheckCircle2,
  ArrowRightLeft,
  XCircle,
  StickyNote,
} from 'lucide-react';
import { SupportClient } from '@ayiland/support-widget';
import type {
  ServerToClient,
  SupportMessageDTO,
  SupportTicketDetailDTO,
  SupportTicketSummaryDTO,
  SupportAgentStatus,
  SupportLanguage,
} from '@ayiland/support-types';
import { useAuthStore, useUIStore } from './store';
import { config } from './config';
import * as api from './api';
import i18n from './i18n';

// ─────────────────────────────────────────────────────────────────────────────
//  Root
// ─────────────────────────────────────────────────────────────────────────────

export const App: React.FC = () => {
  const session = useAuthStore((s) => s.session);
  const language = useUIStore((s) => s.language);

  // Keep i18n in sync with the persisted language preference.
  useEffect(() => {
    if (i18n.language !== language) void i18n.changeLanguage(language);
  }, [language]);

  return session ? <ConsolePage /> : <LoginPage />;
};

// ─────────────────────────────────────────────────────────────────────────────
//  Login
// ─────────────────────────────────────────────────────────────────────────────

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const setSession = useAuthStore((s) => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.login(email, password);
      if (res.user.role !== 'SUPPORT_AGENT' && res.user.role !== 'ADMIN') {
        setError(t('login_error_role'));
        return;
      }
      setSession({
        token: res.accessToken,
        userId: res.user.id,
        email: res.user.email,
        displayName: res.user.displayName,
        role: res.user.role as 'SUPPORT_AGENT' | 'ADMIN',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <form
        onSubmit={onSubmit}
        className="card w-full max-w-md space-y-4 p-6 sm:p-8"
      >
        <div className="text-center">
          <h1 className="text-xl font-bold text-foreground">
            {t('console_title')}
          </h1>
          <p className="mt-1 text-sm text-muted-fg">{t('login_subtitle')}</p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-fg">
            {t('email')}
          </label>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-fg">
            {t('password')}
          </label>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
          />
        </div>
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-danger/30 bg-danger/10 p-2 text-xs font-medium text-danger"
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary w-full"
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('signing_in')}
            </span>
          ) : (
            t('sign_in')
          )}
        </button>
      </form>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Console (post-login)
// ─────────────────────────────────────────────────────────────────────────────

const ConsolePage: React.FC = () => {
  const { t } = useTranslation();
  const session = useAuthStore((s) => s.session)!;
  const logout = useAuthStore((s) => s.logout);
  const theme = useUIStore((s) => s.theme);
  const toggleTheme = useUIStore((s) => s.toggleTheme);
  const language = useUIStore((s) => s.language);
  const setLanguage = useUIStore((s) => s.setLanguage);

  const [scope, setScope] = useState<api.QueueScope>('queued');
  const [tickets, setTickets] = useState<SupportTicketSummaryDTO[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [active, setActive] = useState<SupportTicketDetailDTO | null>(null);
  const [presence, setPresence] = useState<SupportAgentStatus>('ONLINE');
  const [userTyping, setUserTyping] = useState(false);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── WS client (shared with the widget) ──────────────────────────────────
  const client = useMemo(
    () =>
      new SupportClient({
        serviceUrl: config.supportUrl,
        getToken: () => useAuthStore.getState().session?.token,
      }),
    [],
  );

  useEffect(() => {
    client.connect();
    void api.setPresence('ONLINE').catch(() => {});
    return () => {
      client.disconnect();
      void api.setPresence('OFFLINE').catch(() => {});
    };
  }, [client]);

  // ── WS frame handling ───────────────────────────────────────────────────
  useEffect(() => {
    return client.on((evt: ServerToClient) => {
      if (
        evt.type === 'message' &&
        active &&
        evt.message.ticketId === active.id
      ) {
        setActive((a) =>
          a ? { ...a, messages: dedupeAppend(a.messages, evt.message) } : a,
        );
        if (evt.message.senderType === 'USER') {
          client.markRead(active.id, evt.message.id);
        }
      }
      if (
        evt.type === 'typing' &&
        active &&
        evt.ticketId === active.id &&
        evt.senderType === 'USER'
      ) {
        setUserTyping(evt.isTyping);
        if (evt.isTyping) {
          if (typingTimer.current) clearTimeout(typingTimer.current);
          typingTimer.current = setTimeout(() => setUserTyping(false), 3500);
        }
      }
    });
  }, [client, active]);

  // ── Load queue when scope changes ───────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    api.listQueue(scope).then((res) => {
      if (!cancelled) setTickets(res.tickets);
    });
    const t = setInterval(() => {
      api.listQueue(scope).then((res) => {
        if (!cancelled) setTickets(res.tickets);
      });
    }, 15_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [scope]);

  // ── Load active ticket detail + join its room ───────────────────────────
  useEffect(() => {
    if (!activeId) {
      setActive(null);
      return;
    }
    let cancelled = false;
    api.getTicket(activeId).then((res) => {
      if (!cancelled) setActive(res.ticket);
    });
    client.join(activeId);
    return () => {
      cancelled = true;
      client.leave(activeId);
    };
  }, [activeId, client]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const handleClaim = useCallback(async () => {
    if (!active) return;
    const res = await api.claimTicket(active.id);
    setActive(res.ticket);
  }, [active]);

  const handlePatchStatus = useCallback(
    async (status: 'RESOLVED' | 'CLOSED' | 'IN_PROGRESS' | 'WAITING_USER') => {
      if (!active) return;
      const res = await api.patchTicket(active.id, { status });
      setActive(res.ticket);
    },
    [active],
  );

  const handleTransfer = useCallback(async () => {
    if (!active) return;
    const target = window.prompt(t('to_agent_id_placeholder') ?? '');
    if (!target) return;
    const res = await api.transferTo(active.id, target);
    setActive(res.ticket);
  }, [active, t]);

  const handlePresenceChange = useCallback(
    async (next: SupportAgentStatus) => {
      setPresence(next);
      await api.setPresence(next);
    },
    [],
  );

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen flex-col">
      {/* Top bar */}
      <header className="flex items-center justify-between border-b border-border bg-surface px-4 py-2">
        <div className="flex items-center gap-3">
          <span className="font-heading text-base font-bold">
            {t('console_title')}
          </span>
          <span className="hidden text-xs text-muted-fg sm:inline">
            {session.displayName} · {session.email}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={presence}
            onChange={(e) =>
              handlePresenceChange(e.target.value as SupportAgentStatus)
            }
            className="rounded-lg border border-border bg-surface px-2 py-1 text-xs font-medium"
          >
            <option value="ONLINE">{t('presence_online')}</option>
            <option value="BUSY">{t('presence_busy')}</option>
            <option value="AWAY">{t('presence_away')}</option>
            <option value="OFFLINE">{t('presence_offline')}</option>
          </select>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value as 'fr' | 'en' | 'fon')}
            className="rounded-lg border border-border bg-surface px-2 py-1 text-xs font-medium"
          >
            <option value="fr">FR</option>
            <option value="en">EN</option>
            <option value="fon">FON</option>
          </select>
          <button
            type="button"
            onClick={toggleTheme}
            className="btn-ghost p-1.5"
            aria-label={theme === 'dark' ? t('theme_light') : t('theme_dark')}
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </button>
          <button
            type="button"
            onClick={logout}
            className="btn-ghost gap-1.5 px-2 py-1.5 text-xs"
          >
            <LogOut className="h-3.5 w-3.5" />
            {t('logout')}
          </button>
        </div>
      </header>

      {/* 3-column layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left — queue */}
        <aside className="flex w-72 flex-col border-r border-border bg-surface">
          <div className="flex items-center gap-2 border-b border-border p-2">
            <Inbox className="h-4 w-4 text-muted-fg" />
            <span className="text-sm font-semibold">{t('queue_title')}</span>
          </div>
          <div className="flex border-b border-border">
            {(
              ['queued', 'mine', 'active', 'all'] as const
            ).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setScope(s)}
                className={`flex-1 px-2 py-1.5 text-[11px] font-medium transition ${
                  scope === s
                    ? 'bg-brand-600 text-white dark:bg-brand-500'
                    : 'text-muted-fg hover:bg-muted'
                }`}
              >
                {t(`queue_scope_${s}`)}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto scroll-soft">
            {tickets.length === 0 && (
              <div className="p-4 text-center text-xs text-muted-fg">
                {t('queue_empty')}
              </div>
            )}
            {tickets.map((ticket) => (
              <button
                key={ticket.id}
                type="button"
                onClick={() => setActiveId(ticket.id)}
                className={`flex w-full flex-col items-start gap-1 border-b border-border px-3 py-2 text-left transition hover:bg-muted ${
                  activeId === ticket.id ? 'bg-muted' : ''
                }`}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="truncate text-xs font-semibold">
                    {ticket.subject}
                  </span>
                  <PriorityBadge priority={ticket.priority} />
                </div>
                <div className="flex w-full items-center justify-between gap-2 text-[10px] text-muted-fg">
                  <span className="font-mono">{ticket.number}</span>
                  <span>{t(`category_${ticket.category}`)}</span>
                </div>
                {ticket.lastMessagePreview && (
                  <span className="line-clamp-1 text-[11px] text-muted-fg">
                    {ticket.lastMessagePreview}
                  </span>
                )}
              </button>
            ))}
          </div>
        </aside>

        {/* Center — conversation */}
        <main className="flex flex-1 flex-col bg-background">
          {active ? (
            <ConversationPanel
              ticket={active}
              userTyping={userTyping}
              client={client}
              onClaim={handleClaim}
              onTransfer={handleTransfer}
              onResolve={() => handlePatchStatus('RESOLVED')}
              onClose={() => handlePatchStatus('CLOSED')}
              onReload={() =>
                api.getTicket(active.id).then((r) => setActive(r.ticket))
              }
            />
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-fg">
              {t('select_ticket')}
            </div>
          )}
        </main>

        {/* Right — context */}
        <aside className="hidden w-72 flex-col border-l border-border bg-surface lg:flex">
          <div className="border-b border-border p-3 text-sm font-semibold">
            {t('context_title')}
          </div>
          <div className="flex-1 overflow-y-auto scroll-soft p-3 text-xs">
            {active ? <ContextPanel ticket={active} /> : (
              <p className="text-muted-fg">{t('select_ticket')}</p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
  const cls =
    priority === 'URGENT'
      ? 'bg-danger/15 text-danger'
      : priority === 'HIGH'
        ? 'bg-warning/15 text-warning'
        : priority === 'LOW'
          ? 'bg-muted text-muted-fg'
          : 'bg-brand-500/15 text-brand-600 dark:text-brand-400';
  return <span className={`badge ${cls}`}>{priority}</span>;
};

interface ConvProps {
  ticket: SupportTicketDetailDTO;
  userTyping: boolean;
  client: SupportClient;
  onClaim: () => void;
  onTransfer: () => void;
  onResolve: () => void;
  onClose: () => void;
  onReload: () => void;
}
const ConversationPanel: React.FC<ConvProps> = ({
  ticket,
  userTyping,
  client,
  onClaim,
  onTransfer,
  onResolve,
  onClose,
  onReload,
}) => {
  const { t } = useTranslation();
  const [body, setBody] = useState('');
  const [internalNote, setInternalNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [ticket.messages.length, userTyping]);

  const send = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSending(true);
    setError(null);
    try {
      await api.postAgentMessage(ticket.id, {
        body: trimmed,
        internalNote,
      });
      setBody('');
      onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('send_failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-surface px-4 py-2">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold">
            {ticket.subject}
            <span className="badge bg-muted text-muted-fg">
              {t(`status_${ticket.status}`)}
            </span>
          </div>
          <div className="text-[11px] text-muted-fg">
            {t('ticket_meta', {
              number: ticket.number,
              category: t(`category_${ticket.category}`),
            })}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {(ticket.status === 'QUEUED' || !ticket.assignedAgent) && (
            <button onClick={onClaim} className="btn-ghost gap-1 text-xs">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {t('claim')}
            </button>
          )}
          <button onClick={onTransfer} className="btn-ghost gap-1 text-xs">
            <ArrowRightLeft className="h-3.5 w-3.5" />
            {t('transfer')}
          </button>
          <button onClick={onResolve} className="btn-ghost gap-1 text-xs">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t('resolve')}
          </button>
          <button onClick={onClose} className="btn-ghost gap-1 text-xs">
            <XCircle className="h-3.5 w-3.5" />
            {t('close')}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollerRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {ticket.messages.map((m) => (
          <AgentBubble key={m.id} message={m} />
        ))}
        {userTyping && (
          <div className="text-xs text-muted-fg">{t('agent_typing')}</div>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
        className="border-t border-border bg-surface-2 p-2"
      >
        <div className="flex items-end gap-2">
          <textarea
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              client.setTyping(ticket.id, true);
            }}
            onBlur={() => client.setTyping(ticket.id, false)}
            rows={2}
            maxLength={4000}
            placeholder={t('composer_placeholder') ?? ''}
            className="input flex-1 resize-none"
          />
          <button
            type="submit"
            disabled={!body.trim() || sending}
            className="btn-primary h-10 px-3"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
        <label className="mt-2 flex items-center gap-2 text-xs text-muted-fg">
          <input
            type="checkbox"
            checked={internalNote}
            onChange={(e) => setInternalNote(e.target.checked)}
          />
          <StickyNote className="h-3 w-3" />
          {t('internal_note_toggle')}
          <span className="text-[10px]">({t('internal_note_hint')})</span>
        </label>
        {error && (
          <p
            role="alert"
            className="mt-2 rounded-lg border border-danger/30 bg-danger/10 px-2 py-1 text-xs font-medium text-danger"
          >
            {error}
          </p>
        )}
      </form>
    </div>
  );
};

const AgentBubble: React.FC<{ message: SupportMessageDTO }> = ({ message }) => {
  const isUser = message.senderType === 'USER';
  if (message.senderType === 'SYSTEM') {
    return (
      <div className="text-center text-[11px] text-muted-fg">{message.body}</div>
    );
  }
  return (
    <div className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
      <div
        className={[
          'max-w-[75%] rounded-2xl px-3 py-2 text-sm',
          isUser
            ? 'rounded-bl-sm bg-muted text-foreground'
            : message.internalNote
              ? 'rounded-br-sm border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
              : message.senderType === 'AI'
                ? 'rounded-br-sm border border-border bg-surface-2 text-foreground'
                : 'rounded-br-sm bg-brand-600 text-white dark:bg-brand-500',
        ].join(' ')}
      >
        {message.internalNote && (
          <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider">
            note interne
          </div>
        )}
        {message.senderType === 'AI' && (
          <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-500">
            Assistant
          </div>
        )}
        <div className="whitespace-pre-wrap break-words">{message.body}</div>
      </div>
    </div>
  );
};

const ContextPanel: React.FC<{ ticket: SupportTicketDetailDTO }> = ({ ticket }) => {
  const { t } = useTranslation();
  return (
    <dl className="space-y-2">
      <Row k={t('context_user_id')} v={<span className="font-mono">{ticket.id.slice(0, 12)}…</span>} />
      <Row k={t('context_subject')} v={ticket.subject} />
      <Row k={t('context_status')} v={t(`status_${ticket.status}`)} />
      <Row k={t('context_priority')} v={t(`priority_${ticket.priority}`)} />
      <Row
        k={t('context_language')}
        v={(ticket.language as SupportLanguage).toUpperCase()}
      />
      <Row k={t('context_created')} v={new Date(ticket.createdAt).toLocaleString()} />
      {ticket.slaResponseDeadline && (
        <Row
          k={t('context_sla')}
          v={new Date(ticket.slaResponseDeadline).toLocaleString()}
        />
      )}
      {ticket.tags.length > 0 && (
        <div className="pt-2">
          <div className="text-[10px] uppercase text-muted-fg">tags</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {ticket.tags.map((tag) => (
              <span key={tag} className="badge bg-muted text-muted-fg">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </dl>
  );
};

const Row: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div className="flex items-baseline justify-between gap-2">
    <dt className="text-[10px] uppercase text-muted-fg">{k}</dt>
    <dd className="text-right text-xs text-foreground">{v}</dd>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────

function dedupeAppend(
  current: SupportMessageDTO[],
  incoming: SupportMessageDTO,
): SupportMessageDTO[] {
  if (incoming.clientId) {
    const idx = current.findIndex((m) => m.clientId === incoming.clientId);
    if (idx >= 0) {
      const next = current.slice();
      next[idx] = incoming;
      return next;
    }
  }
  if (current.some((m) => m.id === incoming.id)) return current;
  return [...current, incoming];
}

export default App;
